import { forwardRef, type ComponentProps, type ElementRef } from "react";

import { Command } from "cmdk";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "../Button";
import * as Control from "../Control/control.component";
import { controlInputVariants } from "../Control/control.variant";
import { cn } from "../index";
import { Popover } from "../Popover";

/* -------------------------------------------------------------------------------------------------
 * Flag
 * -----------------------------------------------------------------------------------------------*/
const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="h-5 w-7.5 flex overflow-hidden rounded-[5px] bg-primary-dark-gray [&_svg]:h-full [&_svg]:w-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

/* -------------------------------------------------------------------------------------------------
 * ContrySelect
 * -----------------------------------------------------------------------------------------------*/
interface CountryEntry {
  label: string;
  value: RPNInput.Country | undefined;
}

interface CountrySelectProps {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
  noDataMessage?: string;
  placeholder?: string;
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  noDataMessage,
  placeholder,
  onChange,
}: CountrySelectProps) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <Button disabled={disabled}>
        <FlagComponent country={selectedCountry} countryName={selectedCountry} />
      </Button>
    </Popover.Trigger>
    <Popover.Content align="start" sideOffset={15} style={{ width: "auto" }}>
      <Command className="gap-2.5 flex flex-col items-center">
        <Control.Root asChild className="gap-1.25 placeholder:text-primary-dark-gray placeholder:italic">
          <Command.Input placeholder={placeholder} />
        </Control.Root>
        <Command.List className="max-h-75 gap-1.25 flex w-full flex-col overflow-y-auto">
          {noDataMessage && <Command.Empty>{noDataMessage}</Command.Empty>}
          {countryList.map(({ value, label }) =>
            value ? (
              <Command.Item
                key={value}
                className="gap-1.25 p-2.5 flex w-full shrink-0 cursor-pointer items-center rounded-[10px] text-base data-[disabled=true]:cursor-default data-[disabled=true]:text-primary-darker-gray data-[selected=true]:bg-primary-gray [&>span:last-child]:ml-auto [&>span:last-child]:text-primary-dark-gray"
                onSelect={() => onChange(value)}
              >
                <FlagComponent country={value} countryName={label} />
                <span>{label}</span>
                <span>{`+${RPNInput.getCountryCallingCode(value)}`}</span>
              </Command.Item>
            ) : null,
          )}
        </Command.List>
      </Command>
    </Popover.Content>
  </Popover.Root>
);

/* -------------------------------------------------------------------------------------------------
 * Input
 * -----------------------------------------------------------------------------------------------*/
const InputComponent = forwardRef<HTMLInputElement, ComponentProps<"input">>(({ ...props }, ref) => (
  <input {...props} ref={ref} className={controlInputVariants()} />
));

/* -------------------------------------------------------------------------------------------------
 * PhoneInput
 * -----------------------------------------------------------------------------------------------*/
interface InputProps extends Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> {
  onChange?: (value: RPNInput.Value) => void;
  error?: boolean;
}

const Input: React.FC<InputProps> = forwardRef<ElementRef<typeof RPNInput.default>, InputProps>(
  ({ className, error, onChange, ...props }, ref) => (
    <Control.Root asChild error={error} className={cn("gap-1.25", className)}>
      <RPNInput.default
        {...props}
        ref={ref}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputComponent}
        smartCaret={false}
        onChange={value => onChange?.(value ?? "")}
      />
    </Control.Root>
  ),
);
Input.displayName = "PhoneInput";

const { formatPhoneNumber, parsePhoneNumber, isValidPhoneNumber } = RPNInput;

export { Input, FlagComponent, formatPhoneNumber, parsePhoneNumber, isValidPhoneNumber };
export type { InputProps };
